import React, { useState, useEffect } from "react";
import axios from "../../config/adminAxios";
import {
  Box,
  DataTable,
  Text,
  Button,
  Layer,
  Heading,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Spinner,
} from "grommet";
import API_URL from "../../config/config";
import { useNavigate } from "react-router-dom";
import { getProductSkills } from "../../services/getProductSkills";

const UserAssessmentDetails = ({ user_id }) => {
  const [assessmentData, setAssessmentData] = useState([]);
  const [skillsData, setSkillsData] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showRecommendationsModal, setShowRecommendationsModal] =
    useState(false);
  const [recommendationsData, setRecommendationsData] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [summary, skills] = await Promise.all([
          axios.get(`${API_URL}/dashboard/assessmentSummary/${user_id}`),
          axios.get(`${API_URL}/user/getRolesAndSkills`),
        ]);
        setAssessmentData(summary.data.assessmentSummary ?? []);
        setSkillsData(skills.data.sub_product_skills ?? []);
      } catch (err) {
        console.error("Error loading assessment history:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user_id]);

  // Helper to find skill name by topic id
  const getTopicName = (topicId) => {
    const skill = skillsData.find((skill) => skill.id === topicId);
    return skill ? `${skill.product_name} - ${skill.skill_name}` : null;
  };

  const openModalWithTopics = (topics) => {
    setSelectedTopics(topics);
    setShowModal(true);
  };

  const openModalWithQuestions = (questions) => {
    setSelectedQuestions(questions);
    setShowQuestionsModal(true);
  };

  // New: Open modal with recommendations by fetching data
  const openModalWithRecommendations = async (assessment_id) => {
    setSelectedRecommendations(assessment_id);
    setShowRecommendationsModal(true);
    setLoadingRecommendations(true);
    try {
      const response = await getProductSkills({ user_id, assessment_id });
      const recData = response.data.subProducts || [];
      setRecommendationsData(recData);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      setRecommendationsData([]);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const closeModal = () => setShowModal(false);
  const closeQuestionsModal = () => setShowQuestionsModal(false);
  const closeRecommendationsModal = () => {
    setShowRecommendationsModal(false);
    setRecommendationsData([]);
  };

  const getDuration = (startDate, startTime, endDate, endTime) => {
    if (!startDate || !startTime || !endDate || !endTime) {
      return "N/A";
    }

    const startDateTime = new Date(`${startDate.split("T")[0]}T${startTime}`);
    const endDateTime = new Date(`${endDate.split("T")[0]}T${endTime}`);

    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      return "N/A";
    }

    const durationMs = endDateTime - startDateTime;
    const minutes = Math.floor(durationMs / (1000 * 60));
    return minutes < 1 ? `1m` : `${minutes}m`;
  };

  if (loading) {
    return (
      <Box pad="medium" direction="column" align="center">
        <Text>Loading...</Text>
        <Spinner />
      </Box>
    );
  }

  if (assessmentData.length === 0 || skillsData.length === 0) {
    return (
      <Box pad="medium">
        <Text>Please take an assessment to view history</Text>
      </Box>
    );
  }

  const columns = [
    {
      property: "start_date",
      header: <Text>#</Text>,
      render: (datum) => (
        <Text>
          {new Date(datum.start_date).toLocaleDateString()} - {datum.start_time}
        </Text>
      ),
    },
    {
      property: "query_count",
      header: <Text>Number of Questions</Text>,
      render: (datum) => <Text>{datum.query_count}</Text>,
    },
    {
      property: "duration",
      header: <Text>Duration</Text>,
      render: (datum) => (
        <Text>
          {getDuration(
            datum.start_date,
            datum.start_time,
            datum.end_date,
            datum.end_time,
          )}
        </Text>
      ),
    },
    {
      property: "topics",
      header: <Text>Topics</Text>,
      render: (datum) => {
        const topics = [
          getTopicName(datum.topic1),
          getTopicName(datum.topic2),
          getTopicName(datum.topic3),
          getTopicName(datum.topic4),
          getTopicName(datum.topic5),
          getTopicName(datum.topic6),
        ].filter(Boolean);

        return (
          <Button
            label={topics.length > 1 ? "View Topics" : topics[0] || "No Topics"}
            onClick={() => openModalWithTopics(topics)}
          />
        );
      },
    },
    {
      property: "view_questions",
      header: <Text>View Questions</Text>,
      render: (datum) => (
        <Button
          label="View Questions"
          onClick={() => openModalWithQuestions(datum.questions)}
        />
      ),
    },
    {
      property: "view_recommendations",
      header: <Text>View Recommendations</Text>,
      render: (datum) => (
        <Button
          color="brand"
          label="View Course Recommendations"
          onClick={() => openModalWithRecommendations(datum.assesment_id)}
        />
      ),
    },
  ];

  return (
    <Box pad="medium" overflow="auto">
      <Text weight="bold">View all your assessment details here</Text>
      <DataTable
        columns={columns}
        data={assessmentData}
        step={10}
        sortable
        size="medium"
      />

      {showModal && (
        <Layer onEsc={closeModal} onClickOutside={closeModal}>
          <Box
            pad="medium"
            width="large"
            margin="medium"
            align="center"
            overflow="auto"
            style={{ maxHeight: "80vh" }}
          >
            <Text weight="bold" margin={{ bottom: "medium" }}>
              Topics for this assessment
            </Text>
            <Table align="center">
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Topic</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedTopics.map((topic, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Text>{topic}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box align="center" pad="medium">
              <Button label="Close" onClick={closeModal} />
            </Box>
          </Box>
        </Layer>
      )}

      {showQuestionsModal && (
        <Layer onEsc={closeQuestionsModal} onClickOutside={closeQuestionsModal}>
          <Box
            pad="medium"
            width="large"
            margin="medium"
            align="center"
            overflow="auto"
            style={{ maxHeight: "80vh" }}
          >
            <Heading level="3" margin={{ bottom: "small" }}>
              Questions for this assessment
            </Heading>
            <Table align="center">
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Question</Text>
                  </TableCell>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Skill</Text>
                  </TableCell>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Your Answer</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedQuestions.map((question, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Text>{question.question}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{question.skill}</Text>
                    </TableCell>
                    <TableCell>
                      <Text weight="bold">{question.correct}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box align="center" pad="medium">
              <Button label="Close" onClick={closeQuestionsModal} />
            </Box>
          </Box>
        </Layer>
      )}

      {showRecommendationsModal && (
        <Layer
          onEsc={closeRecommendationsModal}
          onClickOutside={closeRecommendationsModal}
        >
          <Box
            pad="medium"
            width="large"
            margin="medium"
            align="center"
            overflow="auto"
            style={{ maxHeight: "80vh" }}
          >
            <Heading level="3" margin={{ bottom: "small" }}>
              Recommendations for this assessment
            </Heading>
            {loadingRecommendations ? (
              <Box
                direction="column"
                align="center"
                justify="center"
                height="small"
              >
                <Spinner />
                <Text margin={{ top: "small" }}>
                  Loading recommendations...
                </Text>
              </Box>
            ) : recommendationsData.length === 0 ? (
              <Text>No further recommendations found for this assessment.</Text>
            ) : (
              <Table align="center">
                <TableHeader>
                  <TableRow>
                    <TableCell scope="col">
                      <Text weight="bold">Product Name</Text>
                    </TableCell>
                    <TableCell scope="col">
                      <Text weight="bold">Skill Name</Text>
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recommendationsData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Text>{item.product_name}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{item.skill_name}</Text>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Box align="center" pad="medium">
              <Button label="Close" onClick={closeRecommendationsModal} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default UserAssessmentDetails;
